import { Localized, useLocalization } from '@fluent/react'
import Button from '@mui/material/Button'
import Fab from '@mui/material/Fab'
import AddIcon from '@mui/icons-material/Add'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { Box, ButtonGroup, Checkbox, Dialog, Paper, TableContainer } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { SysMailTemplateDialog } from './SysMailTemplateDialog'
import { showSuccess } from '../../../notifications/notificationsSlice'
import { AppId } from '../../../../app/appTypes'
import { selectModule } from '../../../modules/moduleSlice'
import { If } from '../../../../app/If'
import { LoadingContainer } from '../../../../app/LoadingContainer'
import { ErrorContainer } from '../../../../app/ErrorContainer'
import { selectSettings } from '../../../settings/settingsSlice'
import { enUS, plPL } from '@mui/x-data-grid/locales'
import { SysMailTemplatesGenerateCodeDialog } from './SysMailTemplatesGenerateCodeDialog'
import { loadSysMailTemplates, selectAllSysMailTemplates, selectSysMailTemplates } from './sysMailTemplatesSlice'
import { loadSysMailFooters, selectAllSysMailFooters, selectSysMailFooters, SysMailFooter } from '../sysFooters/sysMailFootersSlice'
import { QuickSearchToolbar } from '../../../../app/QuickSearchToolbar'
import { DeleteSysMailTemplateDialog } from './DeleteSysMailTemplateDialog'

type DialogState =
    | { type: "none" }
    | { type: "updateTemplate", templateId: AppId | undefined, sysMailFooters: SysMailFooter[], editable: boolean }
    | { type: "newTemplate", sysMailFooters: SysMailFooter[], editable: boolean }
    | { type: "generateCode" }
    | { type: "deleteTemplate", templateId: AppId }

   


const DialogContentDispatcher = ({ state, setter }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>> }) => {
    const dispatch = useAppDispatch()
    const onSuccess = useCallback(() => {
        setter({ type: "none" })
        dispatch(showSuccess("saved"))
    }, [setter, dispatch])
    const onCancel = useCallback(() => {
        setter({ type: "none" })
    }, [setter])

    switch (state.type) {
        case "none":
            return null

        case "updateTemplate":
            return <SysMailTemplateDialog
                id={state.templateId}
                sysMailFooters={state.sysMailFooters}
                editable={state.editable}
                onSaveSuccess={onSuccess}
                onCancel={onCancel}
            />

        case "newTemplate":
            return <SysMailTemplateDialog
            id={undefined}
            sysMailFooters={state.sysMailFooters}
            editable={state.editable}
            onSaveSuccess={onSuccess}
            onCancel={onCancel}
            />
        
        case "generateCode":
            return <SysMailTemplatesGenerateCodeDialog
                onCancel={onCancel}
            />
        
        case "deleteTemplate":
            return <DeleteSysMailTemplateDialog
                templateId={state.templateId}
                onCancel={onCancel}
                onSuccess={onSuccess}
            />
    }
}

export const SysMailTemplates = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const { state: sysMailTemplatesState } = useAppSelector(selectSysMailTemplates)
    const rows = useAppSelector(state => selectAllSysMailTemplates(state))
    const { state: sysMailFootersState } = useAppSelector(selectSysMailFooters)
    const footers = useAppSelector(state => selectAllSysMailFooters(state))
    const currentModule = useAppSelector(state => selectModule(state).currentModule)
    const isSuper = currentModule?.code === "super"

    const [dialog, setDialog] = useState<DialogState>({ type: "none" })
    const handleNewTemplate = useCallback(() => {
        setDialog({
            type: "newTemplate",
            sysMailFooters: footers,
            editable: isSuper
        })
    }, [setDialog, footers, isSuper])

    const handleEditTemplate = useCallback((templateId: AppId) => {
        setDialog({
            type: "updateTemplate",
            templateId,
            sysMailFooters: footers,
            editable: isSuper
        })
    }, [setDialog, footers, isSuper])

    const handleGenerateCode = useCallback(() => {
        setDialog({
            type: "generateCode"
        })
    }, [setDialog])

    const handleDeleteTemplate = useCallback((templateId: AppId) => {
        setDialog({
            type: "deleteTemplate",
            templateId
        })
    }, [setDialog])


    useEffect(() => {
        if (sysMailTemplatesState.type === "empty") {
            dispatch(loadSysMailTemplates())
        }

        if (sysMailFootersState.type === "empty") {
            dispatch(loadSysMailFooters())
        }
    }, [dispatch, sysMailTemplatesState, sysMailFootersState])
    
    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };


    const columns: GridColDef[] =
        [
            {
                field: 'type',
                headerName: l10n.getString("mail-type", null, "_Typ"),
                flex: 1
            },
            {
                field: 'name',
                headerName: l10n.getString("sys-mail-templates-name", null, "_Nazwa"),
                flex: 1,
                valueGetter: (_, row) => {
                    return l10n.getString(`mail-type-${row.type}`, null, row.type)
                }
            },
            {
                field: 'context',
                headerName: l10n.getString("mail-context", null, "_Kontekst"),
                flex: 1
            },
            {
                field: 'isFixed',
                headerName: l10n.getString("sys-mail-templates-is-fixed", null, "_Nieedytowalne"),
                flex: 1,
                renderCell: (params) => <Checkbox disabled checked={params.row.isFixed}/>
            },
            {
                field: 'isInternal',
                headerName: l10n.getString("sys-mail-templates-is-internal", null, "_Wewnętrzny"),
                flex: 1,
                renderCell: (params) => <Checkbox disabled checked={params.row.isInternal}/>
            },
            {
                field: 'actions', type: 'actions', width: 350,
                renderCell: (params) => {
                    return (
                        <ButtonGroup>
                            <Button color="primary" onClick={() => handleEditTemplate(params.row.id)} >
                                <Localized id="edit">
                                    <span>Edytuj</span>
                                </Localized>
                            </Button>
                            <Button color="error" onClick={() => handleDeleteTemplate(params.row.id)} >
                                <Localized id="delete">
                                    <span>Usuń</span>
                                </Localized>
                            </Button>
                        </ButtonGroup>
                    )
                }
            }
        ];


    switch (sysMailTemplatesState.type) {
        case "empty":
        case "loading":
            return <LoadingContainer/>
        case "error":
            return <ErrorContainer error={sysMailTemplatesState.error}/>;
        case "loaded":
            return (
                <>
                    <TableContainer component={Paper}>
                        <DataGrid style={{ minHeight: 100, height: 'calc(100vh - 275px)' }} localeText={getCustomLocaleText()} rows={rows} columns={columns} onRowDoubleClick={({ row }) => handleEditTemplate(row.id)} slots={{ toolbar: QuickSearchToolbar }} />
                        <If condition={isSuper}>
                            <Box sx={{
                                display: 'flex',
                            }}>
                                <Fab sx={{
                                    margin: 1,
                                }}
                                onClick={() => handleNewTemplate()} color="secondary" size="small" >
                                    <AddIcon />
                                </Fab>
                                <Button sx={{ ml: 2 }} onClick={() => handleGenerateCode()}>
                                    <Localized id="default-company-settings-generate-script">
                                        <span>_Wygeneruj skrypt</span>
                                    </Localized>
                                </Button>
                            </Box>
                        </If>
                    </TableContainer>
                    <Dialog open={dialog.type !== "none"} fullWidth maxWidth={dialog.type === "deleteTemplate" ? "sm" : "xl"} keepMounted={false}>
                        <DialogContentDispatcher state={dialog} setter={setDialog} />
                    </Dialog>
                </>
            )
    }
}
